/* import __COLOCATED_TEMPLATE__ from './filter-button.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type OptimizeSuggestionsService from 'embercom/services/optimize-suggestions-service';
import type IntlService from 'ember-intl/services/intl';
import type { ProjectedConversationsRanking } from 'embercom/lib/suggestions/constants';
import {
  type OptimizeSuggestionType,
  type OptimizeSuggestionFilterState,
} from 'embercom/components/reporting/automation/ai-insights/optimize-suggestions/lib/constants';

interface Signature {
  Args: {
    suggestionCount: number;
    isDisabled: boolean;
  };
}

export default class FilterButton extends Component<Signature> {
  @service declare optimizeSuggestionsService: OptimizeSuggestionsService;
  @service declare intl: IntlService;

  @tracked private localReasonTypes: OptimizeSuggestionType[] = [];
  @tracked private localDateRange = '';
  @tracked private localTopics: string[] = [];
  @tracked private localImpactFilter: ProjectedConversationsRanking[] = [];
  @tracked isFilterPanelOpen = false;

  get initialReasonTypes(): OptimizeSuggestionType[] {
    let types = this.optimizeSuggestionsService.filterState?.suggestionTypes ?? [];
    return types.length === 0 ? [] : types;
  }

  get initialDateRange(): string {
    return this.optimizeSuggestionsService.filterState?.date ?? '';
  }

  get initialTopics(): string[] {
    return this.optimizeSuggestionsService.filterState?.topics ?? [];
  }

  get initialImpactFilter(): ProjectedConversationsRanking[] {
    return this.optimizeSuggestionsService.filterState?.impactFilter ?? [];
  }

  @action
  setInitialFilterState(): void {
    this.localReasonTypes = this.initialReasonTypes;
    this.localDateRange = this.initialDateRange;
    this.localTopics = this.initialTopics;
    this.localImpactFilter = this.initialImpactFilter;
  }

  @action
  handleReasonChange(value: OptimizeSuggestionType | 'all'): void {
    if (value === 'all') {
      this.localReasonTypes = [];
    } else if (this.localReasonTypes.includes(value)) {
      this.localReasonTypes = this.localReasonTypes.filter((type) => type !== value);
    } else {
      this.localReasonTypes = [...this.localReasonTypes, value];
    }
  }

  @action
  handleDateRangeChange(value: string): void {
    this.localDateRange = value;
  }

  @action
  handleTopicChange(value: string): void {
    if (value === 'all') {
      this.localTopics = [];
    } else if (this.localTopics.includes(value)) {
      this.localTopics = this.localTopics.filter((topic) => topic !== value);
    } else {
      this.localTopics = [...this.localTopics, value];
    }
  }

  @action
  handleImpactChange(value: ProjectedConversationsRanking | 'all'): void {
    if (value === 'all') {
      this.localImpactFilter = [];
    } else if (this.localImpactFilter.includes(value)) {
      this.localImpactFilter = this.localImpactFilter.filter((level) => level !== value);
    } else {
      this.localImpactFilter = [...this.localImpactFilter, value];
    }
  }

  @action
  handleApplyFilters(): void {
    this.optimizeSuggestionsService.setFilterState(this.currentFilterState);
    this.isFilterPanelOpen = false;
  }

  @action
  toggleFilterPanel() {
    this.isFilterPanelOpen = !this.isFilterPanelOpen;
  }

  @action
  clearFilters(): void {
    this.localReasonTypes = [];
    this.localDateRange = '';
    this.localTopics = [];
    this.localImpactFilter = [];
  }

  get currentFilterState(): OptimizeSuggestionFilterState {
    let suggestionTypes: OptimizeSuggestionType[] = this.localReasonTypes;

    let date: string | undefined = this.localDateRange === 'all' ? undefined : this.localDateRange;

    let topics: string[] = this.localTopics.length === 0 ? [] : this.localTopics;

    return {
      suggestionTypes,
      date,
      topics,
      impactFilter: this.localImpactFilter,
    };
  }

  get isClearButtonDisabled(): boolean {
    return (
      this.localReasonTypes.length === 0 &&
      this.localDateRange === '' &&
      this.localTopics.length === 0 &&
      this.localImpactFilter.length === 0
    );
  }

  get predictedSuggestionsCount(): number {
    return this.optimizeSuggestionsService.predictedSuggestionsCount(this.currentFilterState);
  }

  get filterCount(): number {
    let hasReasonTypes = this.localReasonTypes.length > 0;
    let hasDateRange = this.localDateRange !== '';
    let hasTopics = this.localTopics.length > 0;
    let hasImpactFilter = this.localImpactFilter.length > 0;

    return (
      (hasReasonTypes ? 1 : 0) +
      (hasDateRange ? 1 : 0) +
      (hasTopics ? 1 : 0) +
      (hasImpactFilter ? 1 : 0)
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::OptimizeSuggestions::Suggestions::FilterButton': typeof FilterButton;
    'reporting/automation/ai-insights/optimize-suggestions/suggestions/filter-button': typeof FilterButton;
  }
}
